import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect } from "react";
import Mixpanel from "../utils/mixpanel";
import * as Sentry from "@sentry/react";
import { getStoredGuestSession } from "../services/guestSessionService";

export interface useGetValidAccessTokenReturnType {
  getValidAccessToken: () => Promise<string>;
}

const readGuestToken = (): string => {
  const stored = getStoredGuestSession();
  return stored ? stored.token : "";
};

// Auth0 error codes that only mean "the session has expired, sign in again"
// (no refresh token cached / no Auth0 session). The logout below already
// handles them; reporting them to Sentry just turns every expired session into
// an error issue (FRONTEND-BQ `missing_refresh_token`, `login_required`).
const EXPECTED_SESSION_EXPIRY_ERRORS = new Set([
  "missing_refresh_token",
  "login_required",
]);

const isExpectedSessionExpiry = (error: unknown): boolean =>
  EXPECTED_SESSION_EXPIRY_ERRORS.has((error as { error?: string })?.error ?? "");

const useGetValidAccessToken = (): useGetValidAccessTokenReturnType => {
  const { getAccessTokenSilently, logout, user } = useAuth0();

  const getValidAccessToken = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const decodedToken = JSON.parse(atob(accessToken.split(".")[1]));
      const expiryTime = decodedToken.exp * 1000;
      const isPublicPage = window.location.pathname.includes("explore") || window.location.pathname.includes("share");
      if (expiryTime >= Date.now()) {
        return accessToken;
      } else if (!isPublicPage) {
        logout({ logoutParams: { returnTo: window.location.origin } });
        sessionStorage.clear();
        Mixpanel.reset();
        return "";
      } else {
        return "";
      }
    } catch (error) {
      // Guest mode: no Auth0 session is the *expected* state. Use the stored
      // guest JWT and skip the logout-on-error path that would otherwise nuke
      // the guest session and produce a /welcome → guest → app redirect loop.
      const guestToken = readGuestToken();
      if (guestToken) {
        return guestToken;
      }
      if (!isExpectedSessionExpiry(error)) {
        Sentry.captureException(error);
      }
      const isPublicPage = window.location.pathname.includes("explore") || window.location.pathname.includes("share");
      if (!isPublicPage) {
        logout({ logoutParams: { returnTo: window.location.origin } });
        sessionStorage.clear();
        Mixpanel.reset();
      }
      return "";
    }
  }, [logout, getAccessTokenSilently]);

  useEffect(() => {
    if (user) getValidAccessToken();
  }, [user, getValidAccessToken]);

  return { getValidAccessToken };
};

export default useGetValidAccessToken;
